import React from 'react';
import { fmt } from './adminFormat';

// Single-hue bar chart (vertical columns or horizontal rows) drawn with HTML/CSS, so it follows the
// page's theme tokens without a chart library. Hovering a bar shows its exact value.

const niceMax = (value) => {
    if (!(value > 0)) return 1;
    const magnitude = 10 ** Math.floor(Math.log10(value));
    const step = [1, 2, 2.5, 5, 10].find((s) => s * magnitude >= value) || 10;
    return step * magnitude;
};

const truncate = (label, max = 24) => {
    const text = String(label ?? '');
    return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

const BarChart = ({
    labels = [],
    values = [],
    horizontal = false,
    format = fmt,
    max,
    size = 'normal',
    emptyHint = 'No data yet.',
    ariaLabel,
}) => {
    const numbers = values.map((value) => Number(value) || 0);
    const empty = !numbers.length || numbers.every((value) => !value);
    if (empty) {
        return <div className={`ax-chart ax-chart--${size} ax-chart--empty`}>{emptyHint}</div>;
    }
    const top = max ?? niceMax(Math.max(...numbers));
    const share = (value) => `${Math.max(0, Math.min(100, (value / top) * 100))}%`;

    if (horizontal) {
        return (
            <div className={`ax-hbar ax-chart--${size}`} role="img" aria-label={ariaLabel}>
                {labels.map((label, index) => (
                    <div key={`${label}-${index}`} className="ax-hbar-row" title={`${label}: ${format(numbers[index])}`}>
                        <span className="ax-hbar-label">{truncate(label)}</span>
                        <span className="ax-hbar-track">
                            <span className="ax-hbar-fill" style={{ width: share(numbers[index]) }} />
                        </span>
                        <span className="ax-hbar-value">{format(numbers[index])}</span>
                    </div>
                ))}
            </div>
        );
    }

    const ticks = [1, 0.75, 0.5, 0.25, 0];
    // Show at most ~10 axis labels; dates shorten to MM-DD (the full date is in each bar's tooltip).
    const labelEvery = Math.max(1, Math.ceil(labels.length / 10));
    const axisLabel = (label) => (/^\d{4}-\d{2}-\d{2}$/.test(String(label)) ? String(label).slice(5) : truncate(label, 12));
    return (
        <div className={`ax-vbar ax-chart--${size}`} role="img" aria-label={ariaLabel}>
            <div className="ax-vbar-y" aria-hidden="true">
                {ticks.map((tick) => <span key={tick}>{tick ? format(Math.round(top * tick)) : '0'}</span>)}
            </div>
            <div className="ax-vbar-main">
                <div className="ax-vbar-plot">
                    {ticks.map((tick) => (
                        <span key={tick} className="ax-vbar-grid" style={{ bottom: `${tick * 100}%` }} aria-hidden="true" />
                    ))}
                    {numbers.map((value, index) => (
                        <div key={`${labels[index]}-${index}`} className="ax-vbar-col" title={`${labels[index]}: ${format(value)}`}>
                            <span className="ax-vbar-fill" style={{ height: share(value) }} />
                        </div>
                    ))}
                </div>
                <div className="ax-vbar-x" aria-hidden="true">
                    {labels.map((label, index) => (
                        <span key={`${label}-${index}`}>{index % labelEvery === 0 ? axisLabel(label) : ''}</span>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default BarChart;
